import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsV2Supported } from "src/components/DocumentViewer/useIsV2Supported";
import {
  Document,
  DocumentViewerElement,
  DocumentViewer as DocumentViewerV2,
  boundingRectOfPolygons,
} from "src/components/DocumentViewer/V2";
import type { Point } from "src/components/DocumentViewer/V2/types";
import { DocumentViewer as DocumentViewerV1 } from "src/components/Form/DocumentViewer";
import { ProfileType } from "src/components/Form/utils/analyzeIdFormMatchUtils";
import {
  computeIdentityMatches,
  useImageDimensions,
} from "src/components/Form/utils/analyzeIdHighlightUtils";
import { VerificationDetails } from "src/components/Form/VerificationDetails";
import { useGetDocumentAiAnalyzeIdResults } from "src/generated/hooks/avela-ai/queries/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useDocumentAIResults } from "src/hooks/useDocumentAI";
import { useTimezoneName } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { getSiblings } from "src/scenes/orgAdmin/students/tables/helpers";
import { getGuardians } from "src/services/person";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";

type VerificationModalProps = {
  isOpen: boolean;
  onClose: (options?: {
    showToast?: boolean;
    toastType?: "info" | "success" | "error" | "warning";
  }) => void;
  selectedVerification: {
    verification: AF.FormVerification<AF.WithId>;
    questions: AF.Question<AF.WithId>[];
    answers: Record<string, any>;
    files?: Array<{
      id: string;
      name: string;
      url: string;
      identityAiEligible?: boolean;
    }>;
  } | null;
  initialFileIndex?: number;
  form: GQL.GetFormViewById_form_by_pk;
  currentVerificationStatus: GQL.verification_status_enum | null;
  onVerificationStatusChange: (status: GQL.verification_status_enum) => void;
  canEditVerificationData: boolean;
  onAnswerUpdate: (questionId: string, value: any) => void;
};

export const VerificationModal: React.FC<VerificationModalProps> = ({
  isOpen,
  onClose,
  selectedVerification,
  initialFileIndex = 0,
  form,
  currentVerificationStatus,
  onVerificationStatusChange,
  canEditVerificationData,
  onAnswerUpdate,
}) => {
  const toast = useAvelaToast();
  const timezoneName = useTimezoneName();
  const documentViewerRef = useRef<DocumentViewerElement | null>(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [identityHighlightsVisible, setIdentityHighlightsVisible] =
    useState(true);

  const handleFindInDocument = useCallback(
    (polygons: readonly (readonly Point[])[]) => {
      const boundsPolygon = boundingRectOfPolygons(polygons);
      if (!boundsPolygon?.length) return;
      documentViewerRef.current?.zoomToPolygon(boundsPolygon);
    },
    []
  );

  // Get the current document metadata ID for DocumentAI and AnalyzeID
  const currentFile = selectedVerification?.files?.[selectedFileIndex];
  const documentMetadataId = currentFile?.id;

  // ------ AVELA AI ------
  // Check if AI assistance is enabled for the verification
  const isAiAssistanceEnabled =
    selectedVerification?.verification.is_avela_ai_assisted ?? false;

  // Fetch DocumentAI results for the current document (only if AI assistance is enabled)
  const { resultsData, isLoading: aiLoading } = useDocumentAIResults(
    isAiAssistanceEnabled && documentMetadataId ? documentMetadataId : null
  );

  // Extract data from RemoteData pattern
  const aiResults = resultsData.toNullable();
  const aiError = resultsData.hasError() ? (resultsData as any).error : null;

  // ------ IDENTITY AI ------
  const isV2Supported = useIsV2Supported();
  const isIdentityAiAssisted =
    selectedVerification?.verification.is_identity_ai_assisted ?? false;
  const identityAiEligible = currentFile?.identityAiEligible ?? false;
  const userPermissions = useUserPermissions();
  const hasDocumentAiReadPermission =
    userPermissions.hasOne("document-ai:read");

  const analyzeIdQueryEnabled =
    isIdentityAiAssisted &&
    isV2Supported &&
    !!documentMetadataId &&
    identityAiEligible &&
    hasDocumentAiReadPermission;

  const {
    data: analyzeIdResults,
    isError: isAnalyzeIdError,
    isPending: isAnalyzeIdPending,
    isFetching: isAnalyzeIdFetching,
  } = useGetDocumentAiAnalyzeIdResults(
    {
      path: { document_id: documentMetadataId ?? "" },
    },
    undefined,
    {
      enabled: analyzeIdQueryEnabled,
    }
  );

  const imageDimensions = useImageDimensions(currentFile?.url);

  const { isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds } =
    useSchoolAdmin();
  const profiles = useMemo(
    () => [
      { ...form.person, person_type: ProfileType.Student },
      ...getGuardians(form.person).map((person) => ({
        ...person,
        person_type: ProfileType.Parent,
      })),
      ...getSiblings(
        form.person,
        isElevatedSchoolAdmin,
        applyingSchoolIds,
        attendingSchoolIds
      ).map((person) => ({ ...person, person_type: ProfileType.Sibling })),
    ],
    [form.person, isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds]
  );

  const {
    answerMatches,
    profileMatches,
    highlights: identityMatchHighlights,
  } = computeIdentityMatches(
    analyzeIdResults?.raw_results,
    selectedVerification?.questions,
    selectedVerification?.answers,
    profiles,
    imageDimensions,
    timezoneName
  );

  const imageDimensionsLoading =
    !imageDimensions.loaded && !imageDimensions.unsupportedFileType;
  const identityAiLoading =
    analyzeIdQueryEnabled &&
    (isAnalyzeIdPending || isAnalyzeIdFetching || imageDimensionsLoading);
  const identityAiError =
    isAnalyzeIdError || imageDimensions.unsupportedFileType;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFileIndex(initialFileIndex);
      setIdentityHighlightsVisible(true);
    }
  }, [isOpen, initialFileIndex]);

  const document: Document[] | undefined = useMemo(() => {
    if (!selectedVerification?.files?.length) {
      return undefined;
    }
    return selectedVerification.files.map((file) => ({
      type: "url",
      url: file.url,
      fileName: file.name,
    }));
  }, [selectedVerification?.files]);

  useEffect(() => {
    if (aiError) {
      toast.error({
        title: "Error while fetching Document AI results",
        description:
          aiError?.message ??
          "Please try again later. If the problem persists, please contact support.",
      });
    }
  }, [aiError, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent
        boxShadow="md"
        borderRadius="none"
        p={0}
        h="100vh"
        bg={isV2Supported ? "transparent" : undefined}
      >
        {/* Remove ModalHeader completely and use custom header */}
        <ModalCloseButton top={4} right={4} color="black" zIndex="10" />
        <ModalBody p={0} display="flex" flexDir="column" h="100%">
          {selectedVerification && (
            <Flex flex="1" overflow="hidden">
              {isV2Supported ? (
                document && (
                  <Flex width="60%" height="100%">
                    <DocumentViewerV2
                      ref={documentViewerRef}
                      document={document}
                      documentIndex={selectedFileIndex}
                      onChangeDocumentIndex={setSelectedFileIndex}
                      highlights={identityMatchHighlights}
                      highlightToggle={
                        analyzeIdQueryEnabled
                          ? {
                              show: true,
                              isDisabled:
                                isAnalyzeIdError ||
                                identityMatchHighlights.length === 0,
                              highlightsOn:
                                identityMatchHighlights.length > 0 &&
                                identityHighlightsVisible,
                              onToggle: () =>
                                setIdentityHighlightsVisible((v) => !v),
                            }
                          : undefined
                      }
                    />
                  </Flex>
                )
              ) : (
                <DocumentViewerV1
                  files={selectedVerification.files || []}
                  selectedFileIndex={selectedFileIndex}
                  onFileSelect={setSelectedFileIndex}
                />
              )}
              <VerificationDetails
                verification={selectedVerification.verification}
                questions={selectedVerification.questions}
                answers={selectedVerification.answers}
                status={currentVerificationStatus}
                onStatusChange={onVerificationStatusChange}
                formId={form.id}
                profiles={profiles}
                canEditVerificationData={canEditVerificationData}
                onAnswerUpdate={onAnswerUpdate}
                onClose={(options) => onClose(options)}
                aiResults={aiResults}
                aiLoading={aiLoading}
                aiError={!!aiError}
                identityAiEnabled={analyzeIdQueryEnabled}
                identityAiLoading={identityAiLoading}
                identityAiError={identityAiError}
                identityAiAnswerMatches={answerMatches}
                identityAiProfileMatches={profileMatches}
                onFindInDocument={
                  analyzeIdQueryEnabled ? handleFindInDocument : undefined
                }
              />
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
